<template>
  <ValidationObserver class="d-flex flex-column h-100" v-slot="{handleSubmit, validation, errors}" ref="observer"
                      tag="div">
    <b-form @submit.prevent="handleSubmit(onSubmit)" class="d-flex h-100">
      <SidebarLayout :title="$t('Departments')">
        <template v-slot:buttons>
          <add-new-button v-if="enableSyncWebsiteFromArtisio" class="mr-2" @click="onPostDepartmentsOnWebsiteClick">
            {{ $t('Post Departments On Website') }}
          </add-new-button>
          <add-new-button @click="showModal">
            <font-awesome-icon icon="plus-circle" class="icon"/>
            {{ $t('New Department') }}
          </add-new-button>
        </template>
        <template v-slot:sidebar>
          <Spinner :show="departments.loading"/>
          <ul v-if="!departments.loading" class="nav flex-column">
            <li v-for="(department) in departments.rows"
                :key="department.uuid"
                :class="{ 'active': department.uuid === selectedDepartment.uuid }"
                @click="changeDepartment(department)"
                class="nav-item">
              <a class="nav-link">
                {{ department.name | masterLanguage }}
              </a>
            </li>
          </ul>
        </template>
        <template v-slot:content>
          <div class="bg-white p-2">
            <Spinner :show="departments.loading"/>
            <template v-if="!departments.loading">
              <div v-if="selectedDepartment">
                <h5 style="font-size: 16px;margin-bottom: 1rem">
                  {{ selectedDepartment.name | masterLanguage }}
                </h5>
                <translatable-input-widget :hint="false" :model="model" :fields="model.getTranslatableFieldsArray()"
                                           :errors="errors"/>
                <div class="row">
                  <div class="col-md-12">
                    <input-widget :model="model" attribute="key" disabled/>
                  </div>
                  <div class="col-md-12">
                    <input-widget :model="model" attribute="branches_uuids" multiple :items="branches && branches.rows ? branches.rows : []" display-field="${item.name}" type="select2"/>
                  </div>
                  <div class="col-md-12">
                    <user-select-input-widget :model="model" attribute="specialists_uuids" multiple :items="model.specialists"/>
                  </div>
                  <div class="col-md-12">
                    <input-widget :model="model" attribute="marketing_group_uuid" :items="mailchimpInterests" display-field="${item.name}" value-field="uuid" type="select2"/>
                  </div>
                </div>
                <div class="row my-3">
                  <div class="col-md-6 col-sm-12">
                    <file-uploader :show-preview="false" @onItemsAdd="onImageChange" @onItemRemove="onItemRemove"
                                   :with-tooltip="false"
                                   :items-prop="files" :text="$t('Drag your image here')"
                                   :button-text="$t('Select Image')"
                                   :hint-text="false" :multiple="false" :uploading-progress="uploadingProgress"
                                   :uploading-progress-value="uploadingProgressValue" :accept="fileUploaderAccept"
                                   ref="fileUploader"/>
                  </div>
                </div>
              </div>
              <no-data-available v-if="!selectedDepartment" height="10"/>
            </template>
          </div>
        </template>
        <template v-slot:footer-buttons>
          <delete-button class="mr-2" @click="onDepartmentRemove"/>
          <submit-button/>
        </template>
      </SidebarLayout>
    </b-form>
    <department-modal/>
  </ValidationObserver>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import Spinner from "../../../core/components/common/Spinner";
  import SubmitButton from "../../../core/components/common/SubmitButton";
  import AddNewButton from "../../../core/components/common/AddNewButton";
  import InputWidget from "../../../core/components/input-widget/InputWidget";
  import DeleteButton from "../../../core/components/common/DeleteButton";
  import DepartmentModal from "./DepartmentModal";
  import DepartmentModel from "./Department.model";
  import _ from 'lodash';
  import {Formatter} from "@/core/services/formatter.service";
  import SidebarLayout from "../../../core/components/layout/DefaultLayout/SidebarLayout";
  import {Utils} from "@/core/services/utils.service";
  import DepartmentService from "./DepartmentService";
  import NoDataAvailable from "@/core/components/common/NoDataAvailable";
  import TranslatableInputWidget from "@/core/widgets/TranslatableInputWidget";
  import i18nHelper from "@/core/services/i18n.service";
  import FileUploader from "@/core/components/file-uploader/FileUploader";
  import EventBus from "@/plugins/event-bus";
  import {ON_CLEAR_FILE_UPLOADER_INPUT} from "@/plugins/event-bus/event-bus-names";
  import UserSelectInputWidget from "../../../core/widgets/UserSelectInputWidget.vue";

  const {mapState, mapActions} = createNamespacedHelpers('setup/department');
  const {mapState: mapStateGlobal, mapGetters: mapGettersGlobal} = createNamespacedHelpers('global');
  const {mapState: mapStateBranch} = createNamespacedHelpers('setup/branch');

  export default {
    name: "Department",
    components: {
      UserSelectInputWidget,
      TranslatableInputWidget,
      NoDataAvailable,
      SidebarLayout, DeleteButton, InputWidget, AddNewButton, SubmitButton, Spinner, DepartmentModal, FileUploader
    },
    data() {
      return {
        model: new DepartmentModel(),
        oldState: null,
        decimalSeparators: [
          {key: 'Dot (.)', value: '.'},
          {key: 'Comma (,)', value: ','}
        ],
        thousandSeparators: [
          {key: 'Dot (.)', value: '.'},
          {key: 'Comma (,)', value: ','},
          {key: "Apostrophe (')", value: "'"}
        ],
        uploadingProgressValue: 0,
        uploadingProgress: false,
        files: []
      }
    },
    computed: {
      ...mapState(['departments', 'selectedDepartment', 'selectedDepartmentImage', 'mailchimpInterests']),
      ...mapGettersGlobal(['dropdownCurrencies']),
      ...mapStateGlobal({
        enableSyncWebsiteFromArtisio: state => state.enableSyncWebsiteFromArtisio,
        departmentThumbnailSettings: state => state.departmentThumbnailSettings,
      }),
      ...mapStateBranch(['branches']),
      fileUploaderAccept() {
        return [{formats: this.departmentThumbnailSettings.formats, size: this.departmentThumbnailSettings.max_size}]
      },
    },
    watch: {
      selectedDepartment() {
        if (this.selectedDepartment && this.selectedDepartment.uuid) {
          this.model = new DepartmentModel(_.cloneDeep(this.selectedDepartment));
          this.oldState = this.getState();
          this.files = this.selectedDepartment.thumbnail_image ? [this.selectedDepartment.thumbnail_image] : [];
        }
      }
    },
    methods: {
      ...mapActions(['showModal', 'getDepartments', 'selectDepartment', 'removeDepartment', 'updateDepartment',
          'updateSelectedDepartmentImage', 'getMailchimpInterests']),
      async onSubmit() {
        this.departments.loading = true;
        let formData = new FormData();
        formData.append('uuid', this.model.uuid);
        formData.append('name', JSON.stringify(this.model.name));
        formData.append('key', this.model.key);
        this.model.branches_uuids.forEach((uuid, index) => formData.append(`branches_uuids`, uuid))
        this.model.specialists_uuids.forEach((uuid, index) => formData.append(`specialists_uuids`, uuid))
        // send '', cause null is converted to string 'null'
        formData.append('thumbnail_image_uuid', this.model.thumbnail_image_uuid ? this.model.thumbnail_image_uuid : '');
        if (this.selectedDepartmentImage && this.selectedDepartmentImage.file) {
          formData.append('thumbnail_image', this.selectedDepartmentImage.file);
        }
        if (this.model.marketing_group_uuid) {
          formData.append('marketing_group_uuid', this.model.marketing_group_uuid);
        }
        this.onUploadProgress = true;
        const res = await this.updateDepartment({
          uuid: this.model.uuid, formData, onUploadProgress: (progressEvent) => {
            this.uploadingProgressValue = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
          }
        });
        this.departments.loading = false;
        this.uploadProgress = false;
        this.uploadingProgressValue = 0;
        if (res.success) {
          this.$toast('success', this.$t('Department {name} was successfully updated', {name: Formatter.getByMasterLanguage(this.model.name)}))
        } else {
          if (res.body.name) {
            this.model.setErrors('name_en', res.body.name);
          } else if (res.body.non_field_errors) {
            this.$errorDialog(res.body.non_field_errors[0])
          } else if (res.body[0] && res.body[0].file) {
            this.$errorDialog(res.body[0].file[0]);
          } else {
            this.model.setMultipleErrors(res.body);
          }
        }
      },
      async onDepartmentRemove() {
        this.$confirm(
          this.$i18n.t('Are you sure you want to delete department {name}?', {name: Formatter.getByMasterLanguage(this.selectedDepartment.name)}),
          this.$i18n.t('Department {name} was successfully deleted', {name: Formatter.getByMasterLanguage(this.selectedDepartment.name)}),
          this.removeDepartment,
          this.selectedDepartment.uuid
        );
      },
      async changeDepartment(department) {
        if (this.oldState && this.dataChanged()) {
          this.$confirm(this.$i18n.t('Are you sure you want discard current changes?')).then(async (success) => {
            if (success) this.selectDepartment(_.cloneDeep(department))
          });
        } else {
          this.selectDepartment(_.cloneDeep(department));
        }
        await this.updateSelectedDepartmentImage(department.thumbnail_image);
        EventBus.$emit(ON_CLEAR_FILE_UPLOADER_INPUT);
      },
      getState() {
        if (!this.model) {
          return false;
        }
        const myData = {...this.model};
        delete myData.rules;
        delete myData.errors;
        delete myData.attributeLabels;
        return JSON.stringify(myData);
      },
      dataChanged() {
        return Utils.isDifferent(JSON.parse(this.getState()), JSON.parse(this.oldState));
      },
      async onPostDepartmentsOnWebsiteClick() {
        let response = await DepartmentService.postDepartmentsOnWebsite();
        if (response.success) {
          this.$toast('success', this.$i18n.t('Departments were posted successfully'));
        }
      },
      async onImageChange() {
        if (this.files.length > 1) {
          this.$errorDialog(this.$i18n.t('Only single file is allowed'), this.$i18n.t('Department Thumbnail Image'));
          await this.onItemRemove();
        } else {
          await this.updateSelectedDepartmentImage(this.files[0])
        }
      },
      async onItemRemove() {
        await this.updateSelectedDepartmentImage(null);
        this.model.thumbnail_image_uuid = null;
        this.files = [];
        this.$refs.fileUploader.clearInput();
      }
    },
    async beforeMount() {
      await this.getDepartments();
      await this.getMailchimpInterests();

      if (this.$route.params.department_uuid) {
        this.changeDepartment(this.departments.rows.find(dep => dep.uuid === this.$route.params.department_uuid))
      } else if (this.departments.rows.length > 0) {
        this.changeDepartment(this.departments.rows[0]);
      } else {
        this.oldState = this.getState()
      }
    },
    beforeRouteLeave(to, from, next) {
      if (this.dataChanged()) {
        this.$confirm(this.$i18n.t('Are you sure you want discard current changes?')).then(async (success) => {
          if (success) next();
        });
      } else {
        next();
      }
    },
  }
</script>

<style lang="scss" scoped>
  @import "./../../../scss/index";

  .department-form {
    @include sided-layout;
  }

</style>
